import { forwardRef, useMemo } from 'react';

import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { Tooltip } from '@reckon-web/tooltip';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';

import { useGetVisibleJobStatuses } from '../../hooks/use-get-visible-job-statuses';
import { WorkflowPaletteKey, workflowPaletteDefinition } from '../../theme';

import { Tag } from './tag';

// in status-badge, task-status-badge and job-status-badge
// the more correct export would be import TagProps from ./tag then do something like
// export type StatusBadgeProps = {
//   colour?: string | null;
//   label?: string | null;
// } & Omit<TagProps, 'color' | 'label'>;
// but that requires more changes to the consumers than I'm comfortable with

export type StatusBadgeProps = {
  /** Does not support css. */
  css?: never;
  /** Theme colour name */
  colour?: string | null;
  label?: string | null;
  testId?: string;
};

export const StatusBadge = forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ colour, label, testId = 'status-badge', ...rest }, forwardedRef) => {
    // This deliberately takes a string, because that's what is in the database
    // so we need to do a paranoia check and make sure what's in the database is actually a legit key of the palette
    // This is the most comfortable spot to do it, and is primarily why this component exists
    const verifiedColor = useMemo(() => {
      if (!colour) {
        return undefined;
      }
      if (colour in workflowPaletteDefinition) {
        return colour as WorkflowPaletteKey;
      }
    }, [colour]);

    return (
      <Tag
        color={verifiedColor}
        label={label || 'unknown'}
        data={{ testid: testId }}
        ref={forwardedRef}
        {...rest}
      />
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

export const CountByStatusBadges: React.FC<{
  jobs: { jobStatusId?: string | null }[];
  testId?: string;
}> = ({ jobs, testId = 'job-status-badges' }) => {
  const { data: visibleStatuses, loading, error } = useGetVisibleJobStatuses();

  // build up the count by status for quicker referring to later
  const groupedByStatus = useMemo(
    () =>
      jobs.reduce((result, item) => {
        const existing = result.get(item.jobStatusId || '') || 0;
        result.set(item.jobStatusId || '', existing + 1);
        return result;
      }, new Map<string, number>()),
    [jobs]
  );

  if (loading) {
    return <LoadingDots label="loading statuses" />;
  }
  if (error || !visibleStatuses) {
    return <Text color="critical">error</Text>;
  }

  return (
    <Flex alignItems="center" gap="xsmall" data={{ testid: testId }}>
      {visibleStatuses.map((visibleStatus) => (
        <Tooltip
          key={visibleStatus.cloudId}
          content={visibleStatus.name || 'Unknown'}
        >
          <StatusBadge
            colour={visibleStatus.colour}
            label={String(groupedByStatus.get(visibleStatus.cloudId) || 0)}
            data-testid={makeId(testId, visibleStatus.name || 'unknown')}
          />
        </Tooltip>
      ))}
    </Flex>
  );
};
